import { defineStore } from 'pinia'
import cloneDeep from 'lodash/cloneDeep'
import isEqual from 'lodash/isEqual'
import omitBy from 'lodash/omitBy'

const STORE_NAME = 'search-profiles'

// Note that this storage is set up as localStorage
function normalizeProfileForComparison(profile) {
  return omitBy(profile, (value, key) => value === undefined || value === null || value === '' || key === 'mailingPreference')
}

const checkMailingPreference = (profile, defaultFallback) => {
  if (!Object.hasOwn(profile, 'mailingPreference')) {
    profile.mailingPreference = defaultFallback
  }
  return profile
}

const checkAllMailingPreference = (profiles, defaultFallback) => {
  return profiles.map((profile) => checkMailingPreference(profile, defaultFallback))
}

const hasProfile = (profiles, profile) => {
  const normalizedProfile = normalizeProfileForComparison(profile)
  return profiles.find((element) => isEqual(normalizeProfileForComparison(element), normalizedProfile)) !== undefined
}

export const useSearchProfilesStore = defineStore(STORE_NAME, {
  state: () => ({
    profiles: [],
    globalMailingPreference: false,
  }),
  getters: {
    hasProfile: (state) => (profile) => hasProfile(state.profiles, profile),
    hasProfiles: (state) => state.profiles.length > 0,
    getProfiles: (state) => state.profiles,
  },
  actions: {
    updateProfilesAndMailingPreference({ profiles, preference }) {
      this.setGlobalMailingPreference(preference)
      this.mergeProfiles(profiles)
      this.updateGlobalMailingPreference()
    },
    toggleProfile({ profile }) {
      this.toggleProfileAction(profile)
      this.updateGlobalMailingPreference()
    },
    toggleProfileAction(profile) {
      if (hasProfile(this.profiles, profile)) {
        this.profiles = this.profiles.filter((element) => !isEqual(element, profile))
      } else {
        this.profiles = [...this.profiles, { ...cloneDeep(profile), mailingPreference: false }]
      }
    },
    toggleMailing({ profile }) {
      const index = this.profiles.findIndex((element) => isEqual(element, profile))
      if (index !== -1) {
        this.profiles[index].mailingPreference = !this.profiles[index].mailingPreference
      }
      this.updateGlobalMailingPreference()
    },
    reset() {
      if (this.profiles.length) this.profiles = []
    },
    mergeProfiles(profiles) {
      // Only merge when the current profile is empty and the new profile is not empty
      // Otherwise we choose to ignore the merge request, because there is no way we can determine which version is better
      if (this.profiles.length === 0 && profiles?.length > 0) {
        this.profiles = checkAllMailingPreference(cloneDeep(profiles), this.globalMailingPreference)
      }
    },
    setGlobalMailingPreference(preference) {
      this.globalMailingPreference = preference
    },
    updateGlobalMailingPreference() {
      this.globalMailingPreference = this.profiles.length && this.profiles.reduce((carry, profile) => carry || profile.mailingPreference, false)
    },
  },
  persist: {
    storage: piniaPluginPersistedstate.localStorage(),
    key: `db-${STORE_NAME}`,
  },
})
